import { toast } from "sonner";

/**
 * Trigger a proper browser save-as download for a server-generated PDF.
 *
 * Pipeline:
 *   1. Fetch the PDF as a blob (validates HTTP status + Content-Type so
 *      HTML error pages can never masquerade as a PDF).
 *   2. Prefer the File System Access API (`showSaveFilePicker`) when
 *      available — produces a proper OS-native "Save As" dialog and
 *      works inside sandboxed iframes (e.g. the Emergent preview)
 *      because it only needs a user gesture, not the `allow-downloads`
 *      sandbox flag.
 *   3. Fall back to the classic <a download> click for browsers without
 *      the File System Access API (Firefox / older Safari).
 *   4. Last-resort fallback: open the server URL in a new tab so the
 *      user can at least save manually.
 *
 * Error and progress feedback flows through a single toast so users see
 * "Generating…" → "Saved <filename>" or a clear error reason regardless
 * of which delivery path the browser actually took.
 *
 * @param {string} path - API path (e.g., "/invoices/123/pdf")
 * @param {object} [opts]
 * @param {string} [opts.suggestedName] - Filename seed for the save dialog
 * @param {string} [opts.label] - Human label shown in toasts ("invoice",
 *   "work order") — defaults to "PDF"
 * @param {number} [opts.timeoutMs=60000] - Abort generation after N ms
 * @returns {Promise<boolean>} True when the download was triggered.
 */
export async function downloadPdfUrl(path, opts = {}) {
  const {
    suggestedName = "",
    label = "PDF",
    timeoutMs = 60000,
  } = typeof opts === "string" ? { suggestedName: opts } : opts;

  const baseUrl = process.env.REACT_APP_BACKEND_URL;
  const fullPath = path.startsWith("/api") ? path : `/api${path}`;
  const url = `${baseUrl}${fullPath}`;

  const toastId = toast.loading(`Generating ${label}…`);
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  try {
    // ── Phase 1: Fetch + validate ─────────────────────────────────
    const resp = await fetch(url, { credentials: "include", signal: controller.signal });
    clearTimeout(timer);

    if (!resp.ok) {
      let reason = `HTTP ${resp.status}`;
      try {
        const contentType = resp.headers.get("content-type") || "";
        if (contentType.includes("application/json")) {
          const body = await resp.json();
          reason = body.detail || body.error || body.message || reason;
        } else if (contentType.includes("text/")) {
          const txt = await resp.text();
          reason = (txt.replace(/<[^>]+>/g, " ").trim().slice(0, 160)) || reason;
        }
      } catch { /* leave reason as HTTP code */ }
      throw new Error(reason);
    }

    const ctype = resp.headers.get("content-type") || "";
    if (!ctype.toLowerCase().includes("pdf")) {
      throw new Error(`Server returned ${ctype || "unknown"} instead of a PDF`);
    }

    // Filename precedence: server Content-Disposition > suggested > derived
    const disposition = resp.headers.get("content-disposition") || "";
    const match = disposition.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
    let filename = match ? decodeURIComponent(match[1]) : suggestedName;
    if (!filename) {
      const parts = fullPath.split("/").filter(Boolean);
      filename = `${parts[parts.length - 2] || "document"}.pdf`;
    }
    if (!filename.toLowerCase().endsWith(".pdf")) filename += ".pdf";

    const blob = await resp.blob();
    if (blob.size === 0) throw new Error("Empty file returned by server");

    // ── Phase 2: Save As dialog via File System Access API ─────────
    // Available in Chrome/Edge/Brave/Opera (Chromium-based) since v86.
    // Requires a user gesture (the button click that triggered this
    // function counts). Returns AbortError if the user cancels the
    // dialog — we treat that as a no-op rather than an error.
    if (typeof window.showSaveFilePicker === "function") {
      try {
        const handle = await window.showSaveFilePicker({
          suggestedName: filename,
          types: [{
            description: "PDF document",
            accept: { "application/pdf": [".pdf"] },
          }],
        });
        const writable = await handle.createWritable();
        await writable.write(blob);
        await writable.close();
        toast.success(`Saved ${filename}`, { id: toastId });
        return true;
      } catch (e) {
        if (e?.name === "AbortError") {
          // User clicked Cancel on the Save As dialog — clear the toast
          // silently. They explicitly chose not to save.
          toast.dismiss(toastId);
          return false;
        }
        // SecurityError / NotAllowedError / iframe block → fall through
        // to the classic <a download> path below.
        // eslint-disable-next-line no-console
        console.warn("showSaveFilePicker failed, falling back:", e);
      }
    }

    // ── Phase 3: Classic <a download> fallback ─────────────────────
    // Used for Firefox/Safari (no showSaveFilePicker yet) and as a
    // backup when the File System Access API errors out. target="_blank"
    // helps when the app is iframe-hosted.
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = filename;
    a.target = "_blank";
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(objectUrl), 4000);
    toast.success(`Downloaded ${filename}`, { id: toastId });
    return true;
  } catch (e) {
    clearTimeout(timer);
    let msg = e?.message || String(e);
    if (e?.name === "AbortError") {
      msg = `Generation timed out after ${Math.round(timeoutMs / 1000)}s`;
    }
    toast.error(`Could not download ${label}: ${msg}`, { id: toastId });
    return false;
  }
}
